import { useCallback, useEffect, useRef, useState } from 'react';
import { MONK_SWATCHES } from '@omnialera/ori-signal';
import api, { ApiError } from '../lib/api.js';
import { CoverageChart, PerformanceChart } from '../components/canvas.jsx';
import { Notice, Pill, ago, percent } from '../components/ui.jsx';

const TOKEN_KEY = 'omnialera.ori.operator-token';
const STAMP_KEY = 'omnialera.ori.operator-token-at';

/**
 * How long an unattended console stays open.
 *
 * The token used to live for the life of the tab, which on a laptop that sleeps
 * with the console open is indefinitely. Nothing here is a session — the API has
 * no idea who is holding the token — so the only thing that can end it is this
 * page, and until now nothing did.
 */
const IDLE_MS = 30 * 60 * 1000;

/** Read the stored token, refusing one that has been sitting idle too long. */
function storedToken() {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return '';
    const at = Number(sessionStorage.getItem(STAMP_KEY) ?? 0);
    if (!at || Date.now() - at > IDLE_MS) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(STAMP_KEY);
      return '';
    }
    return token;
  } catch {
    return '';
  }
}

export default function Console({ coverage, recent }) {
  const [token, setToken] = useState(storedToken);
  const [timedOut, setTimedOut] = useState(false);
  const [draft, setDraft] = useState('');
  const [data, setData] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [refusals, setRefusals] = useState(null);
  const [revocations, setRevocations] = useState(null);
  const [exports, setExports] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // load() is declared before touch(), and making one depend on the other would
  // mean reordering the whole component around a hook dependency. A ref is the
  // smaller change and cannot go stale — it is reassigned on every render.
  const touchRef = useRef(() => {});

  const load = useCallback(async (t) => {
    setLoading(true);
    setError(null);
    try {
      const [overview, perf, refs, revs, exp] = await Promise.all([
        api.operatorOverview(t),
        api.operatorPerformance(t),
        api.operatorRefusals(t),
        api.operatorRevocations(t),
        api.operatorExports(t),
      ]);
      // A response that is not the shape this page needs is a failed load, not
      // a successful one — rendering half a dashboard from it is worse than
      // saying it did not work.
      if (!overview || typeof overview !== 'object' || !overview.coverage) {
        throw new ApiError(0, 'bad_response', 'The service returned something this console cannot read.');
      }
      setData(overview);
      setPerformance(perf);
      setRefusals(refs);
      setRevocations(revs);
      setExports(exp);
      touchRef.current();
    } catch (err) {
      setError(err.status === 401 ? 'That token was not accepted.' : err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  /**
   * Deliberately not called signIn. Nobody signs in here: there is no account,
   * no session and no server-side notion of who this is — only a shared secret
   * that grants access to everything behind it. Naming it after a login implies
   * an identity model the system does not have, and the next engineer should
   * not have to read the API to find that out.
   */
  const openConsole = (event) => {
    event.preventDefault();
    const next = draft.trim();
    if (!next) return;
    try {
      sessionStorage.setItem(TOKEN_KEY, next);
      sessionStorage.setItem(STAMP_KEY, String(Date.now()));
    } catch {
      /* session storage unavailable; the token stays in memory only */
    }
    setTimedOut(false);
    setToken(next);
    setDraft('');
  };

  /** Forget the token here and now. The API is not told, because it was never told. */
  const closeConsole = useCallback((reason = 'manual') => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(STAMP_KEY);
    } catch {
      /* nothing to clear */
    }
    setTimedOut(reason === 'idle');
    setToken('');
    setData(null);
    setPerformance(null);
    setRefusals(null);
    setRevocations(null);
    setExports(null);
    setError(null);
  }, []);

  /**
   * Close an idle console.
   *
   * Checked on a timer AND on return to visibility, because a sleeping laptop
   * suspends timers: without the visibility check a machine that slept for a
   * day would wake with the console still open and the countdown barely moved.
   */
  useEffect(() => {
    if (!token) return undefined;
    const check = () => {
      let at = 0;
      try {
        at = Number(sessionStorage.getItem(STAMP_KEY) ?? 0);
      } catch {
        return;
      }
      if (at && Date.now() - at > IDLE_MS) closeConsole('idle');
    };
    const timer = setInterval(check, 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [token, closeConsole]);

  /** Any successful load is the operator being present. */
  const touch = useCallback(() => {
    try {
      sessionStorage.setItem(STAMP_KEY, String(Date.now()));
    } catch {
      /* nothing to record */
    }
  }, []);
  touchRef.current = touch;

  if (!token || (!data && error)) {
    return (
      <section>
        <div className="vh">
          <p className="eyebrow">For OmniAlera</p>
          <h1>Operator console</h1>
          <p>Aggregate figures only. There is deliberately no endpoint here that can re-identify a contributor.</p>
        </div>
        {timedOut ? (
          <Notice kind="warn" title="Console closed">
            It had been idle for thirty minutes, so the token was discarded. Paste it again to carry on.
          </Notice>
        ) : null}
        {error ? <Notice kind="bad" title="Not authorised">{error}</Notice> : null}
        <form className="card" onSubmit={openConsole} style={{ maxWidth: '520px' }}>
          <div className="field">
            <label htmlFor="op-token">Operator token</label>
            <input
              id="op-token"
              type="password"
              value={draft}
              autoComplete="off"
              placeholder="Paste the operator token"
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          <div className="btnrow" style={{ marginTop: 'var(--s2)' }}>
            <button type="submit" className="btn" disabled={!draft.trim()}>
              Open the console
            </button>
          </div>
          <p style={{ fontSize: '0.9rem', color: 'var(--faint)', margin: 'var(--s2) 0 0', lineHeight: 1.6 }}>
            Held in this tab only, never written to disk, and gone when the tab closes.
          </p>
        </form>
      </section>
    );
  }

  const cov = data?.coverage ?? coverage;
  const openRecalls = data?.exports?.openRecalls ?? 0;

  return (
    <section>
      <div className="vh">
        <p className="eyebrow">For OmniAlera</p>
        <div className="row">
          <h1 style={{ margin: 0 }}>Operator console</h1>
          <button type="button" className="btn ghost" onClick={() => closeConsole('manual')}>
            Close the console
          </button>
        </div>
        <p>Where the corpus actually stands, what it is worth, and which revenue rails it has not yet unlocked.</p>
        <p style={{ fontSize: '0.88rem', color: 'var(--faint)', margin: 0 }}>
          One shared token, so this console cannot say who you are — and neither can the audit trail. It closes itself
          after thirty minutes idle.
        </p>
      </div>

      {openRecalls > 0 ? (
        <Notice kind="bad" title={`${openRecalls} outstanding withdrawal${openRecalls === 1 ? '' : 's'}`}>
          People have withdrawn from the research group after an extract containing them was already delivered. Every
          partner below must be notified before another extract ships.
        </Notice>
      ) : null}

      <div className="con">
        <div className="panel span12">
          <div className="ph">
            <span className="t">Corpus position</span>
            <span className="x">{loading ? 'refreshing…' : `live · updated ${ago(cov?.generatedAt ?? Date.now())}`}</span>
          </div>
          <div className="bigstats">
            <Big value={cov ? cov.readings.toLocaleString() : '—'} label="Readings in corpus" detail={`${cov?.devices ?? 0} contributors`} />
            <Big
              value={cov ? percent(cov.underserved.share, 1) : '—'}
              label="Share from tones 7–10"
              detail="published datasets: under 25% for tones 4–10"
            />
            <Big
              value={cov ? percent(cov.underserved.lockRate, 0) : '—'}
              label="Success rate there"
              detail="the number that has to move"
            />
            <Big value={cov ? cov.creditsOwed.toLocaleString() : '—'} label="Credits owed" detail="a liability, not revenue" />
            <Big value={data?.cohort?.consented?.toLocaleString?.() ?? '—'} label="In the research group" detail="the sellable population" />
          </div>
        </div>

        <div className="panel span8">
          <div className="ph">
            <span className="t">Corpus coverage by skin tone</span>
            <span className="x">live · shared store</span>
          </div>
          <div className="pb">
            <div className="legend">
              <span><i style={{ background: '#f87f94' }} />Ori corpus</span>
              <span><i style={{ background: '#ebb673' }} />Published datasets</span>
              <span><i style={{ background: '#63ccab' }} />World population</span>
            </div>
            <CoverageChart coverage={cov} />
          </div>
        </div>

        <div className="panel span4">
          <div className="ph">
            <span className="t">Live contributions</span>
            <span className="x">{recent?.length ? `${recent.length} recent` : '—'}</span>
          </div>
          <div className="pb feed">
            {!recent?.length ? (
              <p className="skeleton">No contributions yet.</p>
            ) : (
              recent.map((c, i) => (
                <div className="f" key={`${c.receivedAt}-${i}`}>
                  <span className="sw" style={{ background: MONK_SWATCHES[c.monkTone - 1] }} />
                  <span>
                    Tone {c.monkTone} ·{' '}
                    {c.locked ? (
                      `found in ${c.seconds.toFixed(1)}s`
                    ) : (
                      <span style={{ color: 'var(--pulse)' }}>no clear signal</span>
                    )}
                  </span>
                  <time>{ago(c.receivedAt)}</time>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="panel span6">
          <div className="ph">
            <span className="t">Performance by tone</span>
            <span className="x">from donated readings</span>
          </div>
          <div className="pb">
            <div className="legend">
              <span><i style={{ background: '#f87f94' }} />Conventional estimator error</span>
              <span><i style={{ background: '#63ccab' }} />Ori success rate</span>
            </div>
            <PerformanceChart byTone={performance?.byTone} />
          </div>
        </div>

        <div className="panel span12">
          <div className="ph">
            <span className="t">Why readings fail</span>
            <span className="x">
              {refusals?.byTone?.length
                ? `${refusals.byTone.reduce((n, r) => n + r.refusals, 0)} refusals · scanner ${refusals.scannerVersion}`
                : 'no refusals yet'}
            </span>
          </div>
          <div className="pb tscroll">
            <RefusalTable refusals={refusals} />
          </div>
        </div>

        <div className="panel span6">
          <div className="ph">
            <span className="t">Research extracts</span>
            <span className="x">{exports?.exports?.length ?? 0} built</span>
          </div>
          <div className="pb tscroll">
            {!exports?.exports?.length ? (
              <p className="skeleton">No extracts built yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Rows</th>
                    <th>Subjects</th>
                    <th>Recalls</th>
                  </tr>
                </thead>
                <tbody>
                  {exports.exports.slice(0, 8).map((e) => (
                    <tr key={e.id}>
                      <td>
                        <b>{e.name}</b>
                        <br />
                        <span style={{ color: 'var(--faint)', fontSize: '0.85rem' }}>{e.requestedBy}</span>
                      </td>
                      <td className="n">{e.rowCount ?? '—'}</td>
                      <td className="n">{e.subjectCount ?? '—'}</td>
                      <td className="n">
                        {e.openRecalls ? <Pill kind="bad">{e.openRecalls}</Pill> : <span style={{ color: 'var(--faint)' }}>0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {revocations?.revocations?.length ? (
          <div className="panel span12">
            <div className="ph">
              <span className="t">Withdrawals to notify</span>
              <span className="x">work this queue before shipping anything else</span>
            </div>
            <div className="pb tscroll">
              <table>
                <thead>
                  <tr>
                    <th>Extract</th>
                    <th>Partner</th>
                    <th>Rows affected</th>
                    <th>Withdrawn</th>
                  </tr>
                </thead>
                <tbody>
                  {revocations.revocations.map((r) => (
                    <tr key={r.id}>
                      <td>{r.exportName}</td>
                      <td>{r.requestedBy}</td>
                      <td className="n">{r.affectedRows}</td>
                      <td className="n">{ago(r.revokedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="panel span12">
          <div className="ph">
            <span className="t">Rail readiness</span>
            <span className="x">what each revenue rail still needs</span>
          </div>
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Rail</th>
                  <th>Gate</th>
                  <th>Current</th>
                  <th>Target</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rails ?? []).map((rail) => (
                  <tr key={rail.rail + rail.gate}>
                    <td>
                      <b>{rail.rail}</b>
                      <br />
                      <span style={{ color: 'var(--faint)', fontSize: '0.85rem' }}>{rail.note}</span>
                    </td>
                    <td>{rail.gate}</td>
                    <td className="n">{formatRailValue(rail.current, rail.unit)}</td>
                    <td className="n">{formatRailValue(rail.target, rail.unit)}</td>
                    <td>
                      <Pill kind={rail.status === 'ready' ? 'ok' : rail.status === 'building' ? 'mid' : 'off'}>
                        {rail.status}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="foot">
        <p>
          <strong>What is real.</strong> Every figure above is computed from the live database. The signal engine that
          produced the readings is the same module the app runs in your browser and the same one the test suite proves
          against known heart rates.
        </p>
        <p>
          <strong>What is not here.</strong> There is no way, from this console, to see who contributed anything. That
          capability was never built, because the business does not need it and its existence would undermine the only
          promise that matters.
        </p>
      </div>
    </section>
  );
}

/**
 * The refusal breakdown.
 *
 * A lock rate says how often the engine failed. It cannot say whether the
 * frames were unusable or the pulse was unreachable, and those two point at
 * completely different work: better capture, or better signal processing. This
 * table is the only place that distinction is visible.
 *
 * Reasons overlap — one refusal can fail several criteria at once — so the
 * columns are counts, never a partition, and they are not made to sum.
 */
function RefusalTable({ refusals }) {
  if (!refusals) return <p className="skeleton">Loading…</p>;
  if (!refusals.byTone.length) return <p className="skeleton">No refusals recorded yet.</p>;

  const t = refusals.thresholds;
  return (
    <>
      <table>
        <thead>
          <tr>
            <th scope="col">Tone</th>
            <th scope="col">Refusals</th>
            <th scope="col" title={`Fewer than ${Math.round(t.minFrameAcceptance * 100)}% of frames usable`}>
              Capture
            </th>
            <th scope="col" title={`Peak prominence below ${t.prominence}`}>Weak peak</th>
            <th scope="col" title={`Harmonic support below ${t.harmonic}`}>No harmonic</th>
            <th scope="col" title={`Region agreement below ${Math.round(t.agreement * 100)}%`}>
              Regions differ
            </th>
            <th scope="col" title={`Methods more than ${t.methodToleranceBpm} bpm apart`}>Methods differ</th>
            <th scope="col" title="Closest any refusal at this tone came to the prominence bar">
              Closest miss
            </th>
          </tr>
        </thead>
        <tbody>
          {refusals.byTone.map((r) => {
            const near = r.bestProminence !== null && r.bestProminence >= t.prominence * 0.9;
            return (
              <tr key={r.monkTone}>
                <th scope="row">{r.monkTone}</th>
                <td className="mono">{r.refusals}</td>
                <td className="mono">{r.reasons.capture || '—'}</td>
                <td className="mono">{r.reasons.peak || '—'}</td>
                <td className="mono">{r.reasons.harmonic || '—'}</td>
                <td className="mono">{r.reasons.regions || '—'}</td>
                <td className="mono">{r.reasons.methods || '—'}</td>
                <td className="mono" style={near ? { color: 'var(--warn)' } : undefined}>
                  {r.bestProminence === null ? '—' : r.bestProminence.toFixed(1)}
                  {near ? ' ◂' : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="note">
        A reading must clear every criterion, so one refusal can appear in several columns — these are counts, not a
        split. <b>Capture</b> means the frames were never usable and no algorithm work would have helped;{' '}
        <b>weak peak</b> is the melanin ceiling this corpus exists to move. Closest miss counts only refusals where
        the peak was the problem — a reading that failed on motion still has a healthy peak and would otherwise make a
        tone look nearer the ceiling than it is. A miss marked <span style={{ color: 'var(--warn)' }}>◂</span> came
        within 10% of the {t.prominence.toFixed(1)} bar.
      </p>
    </>
  );
}

function Big({ value, label, detail }) {
  return (
    <div>
      <span className="v">{value}</span>
      <span className="k">{label}</span>
      <span className="d">{detail}</span>
    </div>
  );
}

function formatRailValue(value, unit) {
  if (value === null || value === undefined) return '—';
  if (unit === 'rate') return percent(value, 0);
  return typeof value === 'number' ? value.toLocaleString() : String(value);
}
